// City passport (docs/APP_REFERENCE.md "City passport"): how long this browser has listened in each city. Five minutes
// in a city stamps it: a toast once, a mark on its card in the picker and on the globe, and the picker's Passport view
// (passportRows) lists every city's stamp or progress. A first minute with no stamp yet gets a hint (hintOf). A later
// visit is welcomed back with it (welcomeBack: the line under the play prompt). Kept in
// localStorage as `lofi-cities:passport`, { v: 1, s: { tokyo: 412, … }, last }: whole seconds per city and when music
// last played (epoch ms). A signed-in visitor's passport is the account's too (site/js/live/account.js). Pure but for the clocks and storage it's handed, so node can test it: node --test tests/

export const STAMP_S = 5 * 60;                     // listening in a city that earns its stamp
export const SAVE_MS = 15 * 1000;                  // storage is written at most this often (and as the page goes)
export const WELCOME_MIN_S = 60;                   // a welcome back needs a minute of listening in all,
export const WELCOME_GAP_MS = 30 * 60 * 1000;      // the last of it half an hour ago or more: a reload mid-visit isn't one

/** A stored passport, well-formed: whole seconds per city (anything else left out) and `last` (0: never), else empty. */
export function passportOf(raw) {
  const s = {}, secs = raw?.s && typeof raw.s === 'object' && !Array.isArray(raw.s) ? raw.s : {};
  for (const [id, v] of Object.entries(secs)) if (id !== '__proto__' && Number.isFinite(v) && v >= 1) s[id] = Math.floor(v);
  return { v: 1, s, last: Number.isFinite(raw?.last) && raw.last > 0 ? raw.last : 0 };
}

/** The stamped cities among `ids` (the registry's, so a city that's gone doesn't count), in their order. */
export const stampsOf = (p, ids, stampS = STAMP_S) => ids.filter(id => (p.s[id] ?? 0) >= stampS);

/** Seconds as the welcome says them: "1 min" (under a minute too) … "59 min", "2 h", "2 h 14 m" (whole minutes). */
export function duration(s) {
  const m = Math.max(1, Math.floor(s / 60));
  return m < 60 ? `${m} min` : m % 60 ? `${Math.floor(m / 60)} h ${m % 60} m` : `${m / 60} h`;
}

/**
 * The picker's Passport view: every city in `ids` (the registry's) with its seconds and whether it's stamped. Stamped
 * first, then begun, the most listened first in each; the rest keep the registry's order.
 */
export function passportRows(p, ids, stampS = STAMP_S) {
  const rank = s => (s >= stampS ? 0 : s > 0 ? 1 : 2);
  return ids.map((id, i) => ({ id, s: p.s[id] ?? 0, i }))
    .sort((a, b) => rank(a.s) - rank(b.s) || b.s - a.s || a.i - b.i)
    .map(({ id, s }) => ({ id, s, stamped: s >= stampS }));
}

/** A city's line in the Passport view: its time once stamped ("15 min"), else how far along ("2 of 5 min"). */
export function progressLine(s, stampS = STAMP_S) {
  if (s >= stampS) return duration(s);
  return stampS >= 60 ? `${Math.floor(s / 60)} of ${Math.floor(stampS / 60)} min` : `${Math.floor(s)} of ${stampS} s`;   // ?passport=fast
}

/**
 * The first-minute hint (a toast, once per browser): the seconds `city` still needs for its stamp, once it has a fifth
 * of them (a minute) while no city is stamped yet; else null.
 */
export function hintOf(p, city, ids, stampS = STAMP_S) {
  if (!ids.includes(city) || stampsOf(p, ids, stampS).length) return null;
  const s = p.s[city] ?? 0;
  return s >= stampS / 5 && s < stampS ? stampS - s : null;
}

/**
 * A returning visitor's greeting, or null: a first visit, under WELCOME_MIN_S in all, or music within WELCOME_GAP_MS
 * (a reload). It says their time in `city` (a minute or more there), else in all and in how many cities (or the one
 * city's name), then their stamps once there's one. `lines` go from the whole line to "welcome back" alone, for the
 * page to show the longest that fits (the dock's second line is narrow: about 13 characters on a 375 px phone).
 * `cities` is the registry ({ id, name }), `now` epoch ms.
 */
export function welcomeBack(p, city, cities, now, stampS = STAMP_S) {
  const been = cities.filter(c => (p.s[c.id] ?? 0) > 0), total = been.reduce((t, c) => t + p.s[c.id], 0);
  if (total < WELCOME_MIN_S || !(now - p.last >= WELCOME_GAP_MS)) return null;
  const here = cities.find(c => c.id === city), cityS = here ? p.s[city] ?? 0 : 0;
  const where = cityS >= 60 ? `${duration(cityS)} in ${here.name}`
    : been.length === 1 ? `${duration(total)} in ${been[0].name}` : `${duration(total)} in ${been.length} cities`;
  const stamps = stampsOf(p, cities.map(c => c.id), stampS).length;
  const lines = [
    `welcome back · ${where}`,
    `welcome back · ${cityS >= 60 ? `${duration(cityS)} here` : `${duration(total)} so far`}`,
    'welcome back',
  ];
  if (stamps) lines.unshift(`${lines[0]} · ${stamps} of ${cities.length} stamped`);
  return { lines, city_s: cityS, total_s: total, stamps };
}

/**
 * This tab's listening, counted into the passport. Call `at(city, playing)` whenever the city on screen or whether
 * music plays may have changed, and on a timer: the time since the last call goes to the city on screen then, if music
 * was playing, so a switch never credits the new city with the old one's time. `load()` and `save(p)` are the storage
 * (either may throw, or load junk); `at` saves at most every SAVE_MS, `flush()` now. A save adds this tab's new seconds
 * to what's stored by then (another tab's too), and keeps what this tab has seen if the storage lost it.
 * `onStamp(id)` hears a city reach `stampS` in this tab. `now` is a monotonic clock in ms, `epoch` the wall clock.
 */
export function createPassport({ load, save, onStamp = () => {}, stampS = STAMP_S, now = () => performance.now(), epoch = () => Date.now() }) {
  const read = () => { try { return passportOf(load()); } catch { return passportOf(null); } };
  let stored = read(), ms = {}, city = null, playing = false, since = now(), savedAt = since;
  const secondsIn = id => (stored.s[id] ?? 0) + Math.floor((ms[id] ?? 0) / 1000);
  function credit() {
    const t = now(), d = t - since;
    since = t;
    if (!playing || !city || !(d > 0)) return;
    const before = secondsIn(city);
    ms[city] = (ms[city] ?? 0) + d;
    if (before < stampS && secondsIn(city) >= stampS) onStamp(city);
  }
  function flush() {
    credit();
    savedAt = now();
    const adds = Object.entries(ms).map(([id, v]) => [id, Math.floor(v / 1000)]).filter(([, s]) => s > 0);
    if (!adds.length) return;                        // no new listening: nothing to write, `last` stays
    const p = read();
    for (const [id, s] of Object.entries(stored.s)) p.s[id] = Math.max(p.s[id] ?? 0, s);   // cleared or unavailable storage
    for (const [id, s] of adds) { p.s[id] = (p.s[id] ?? 0) + s; ms[id] -= s * 1000; }
    p.last = epoch();
    stored = p;
    try { save(p); } catch { /* storage unavailable: this tab still counts */ }
  }
  return {
    /** From now on the city on screen is `id` (or none) and music is `playing` (or not). */
    at(id, isPlaying) {
      credit();
      city = id ?? null;
      playing = !!isPlaying;
      if (now() - savedAt >= SAVE_MS) flush();
    },
    flush,
    /** The stored passport again, as another writer left it (an account's sync, site/js/live/account.js): it may have
     * less in a city than this tab saw, after another account's sign-in. This tab's unsaved seconds still count. */
    reload() { stored = read(); },
    /** The passport as stored, with this tab's whole seconds not saved yet added. */
    get view() {
      const s = { ...stored.s };
      for (const id of Object.keys(ms)) if (secondsIn(id) > 0) s[id] = secondsIn(id);
      return { v: 1, s, last: stored.last };
    },
  };
}
